// src/components/layout/Header.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../ui/Logo';

const NAV_ITEMS = ['Home', 'About', 'Services', 'Expertise', 'Products', 'Open Source', 'Blog', 'Contact'];

const toSectionId = (item) => item.toLowerCase().replace(/\s+/g, '-');

const Header = ({ onNavigate, currentPage, onSectionChange, activeSection: externalActiveSection }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [internalActiveSection, setInternalActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const activeSection = externalActiveSection !== undefined ? externalActiveSection : internalActiveSection;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      if (onSectionChange) return;

      const sections = ['home', 'about', 'services', 'products', 'contact'];
      for (const section of [...sections].reverse()) {
        const element = document.getElementById(section);
        if (element && window.scrollY >= element.offsetTop - 100) {
          setInternalActiveSection(section);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [onSectionChange]);

  const handleNavigation = (item) => {
    const sectionId = toSectionId(item);

    if (sectionId === 'blog') {
      navigate('/blog');
      setMobileMenuOpen(false);
      return;
    }

    if (onSectionChange) {
      onSectionChange(sectionId);
      setMobileMenuOpen(false);
      return;
    }

    if (location.pathname !== '/') {
      navigate('/', { state: { section: sectionId } });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        window.scrollTo({ top: element.offsetTop - 80, behavior: 'smooth' });
        setInternalActiveSection(sectionId);
      }
    }
    setMobileMenuOpen(false);
  };

  // Home drives panels via onSectionChange; standalone pages (open-source,
  // blog) have their own scroll + dark heroes, so keep the header solid there
  // too — a transparent, taller header reads as disconnected from the site.
  const alwaysOpaque = !!onSectionChange || currentPage === 'open-source' || currentPage === 'blog';

  const isActive = (item) => {
    const id = toSectionId(item);
    return (currentPage === 'blog' && item === 'Blog') ||
      (currentPage === 'home' && activeSection === id);
  };

  return (
    <header className={`fixed w-full z-50 transition-all duration-200 ${(isScrolled || alwaysOpaque) ? 'bg-white shadow-sm py-2' : 'bg-transparent py-4'}`}>
      <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
        <Logo />

        {/* Desktop Navigation */}
        <nav className="hidden md:block">
          <ul className="flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <li key={item}>
                <button
                  onClick={() => handleNavigation(item)}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors duration-200 ${
                    isActive(item)
                      ? 'text-primary-dark font-semibold bg-blue-50'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 font-medium'
                  }`}
                >
                  {item}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-gray-500 focus:outline-none p-1"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <ul className="py-1">
            {NAV_ITEMS.map((item) => (
              <li key={item}>
                <button
                  onClick={() => handleNavigation(item)}
                  className={`block w-full text-left py-2.5 px-6 text-sm ${
                    isActive(item)
                      ? 'text-primary-dark font-semibold bg-blue-50'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {item}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
};

export default Header;
